/* eslint-disable react/jsx-props-no-spreading */
import {
    useEffect, useRef, useState,
} from 'react'
import {
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger,
} from '@mui/x-data-grid-pro'

import SearchIcon from '@mui/icons-material/Search'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import CancelIcon from '@mui/icons-material/Cancel'
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    useResizeObserver,
} from 'usehooks-ts'

import {
    StyledQuickFilter,
    StyledTextField,
    StyledToolbarButton,
    StyledWrapper,
} from './QuickFilterToolbarItem.styled'

type Props = {
    useDynamicWidth: boolean
}

export const DEFAULT_EXPANDED_INPUT_WIDTH = 260

export default function QuickFilterToolbarItem({
    useDynamicWidth,
}: Readonly<Props>) {
    const containerRef = useRef<HTMLDivElement>(null)
    const {
        width,
    } = useResizeObserver({
        ref: containerRef,
    })

    const [
        maxWidth,
        setMaxWidth,
    ] = useState(DEFAULT_EXPANDED_INPUT_WIDTH)
    const [
        isExpanded,
        setIsExpanded,
    ] = useState(false)

    useEffect(() => {
        if (width && !isExpanded && useDynamicWidth) {
            setMaxWidth(width - 10)
        }
    }, [
        width,
        isExpanded,
        useDynamicWidth,
    ])

    return (
        <StyledWrapper ref={containerRef}>
            <StyledQuickFilter>
                <QuickFilterTrigger
                    render={(triggerProps, state) => {
                        return (
                            <Tooltip
                                title="Search"
                                enterDelay={0}
                            >
                                <StyledToolbarButton
                                    {...triggerProps}
                                    ownerState={{
                                        expanded: state.expanded,
                                    }}
                                    ref={(node) => {
                                        if (node) {
                                            setIsExpanded(state.expanded)
                                        }
                                        // it is needed for proper positioning
                                        (triggerProps.ref as (el) => void)(node)
                                    }}
                                    color="default"
                                    aria-disabled={state.expanded}
                                    data-testid="search-filter-btn"
                                >
                                    <SearchIcon fontSize="small" />
                                </StyledToolbarButton>
                            </Tooltip>
                        )
                    }}
                />
                <QuickFilterControl
                    render={({
                        ref: inputRef, ...controlProps
                    }, state) => {
                        return (
                            <StyledTextField
                                {...controlProps}
                                ownerState={{
                                    expanded: state.expanded,
                                    maxWidth,
                                }}
                                inputRef={inputRef}
                                aria-label="Search"
                                placeholder="Search..."
                                size="small"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: state.value ? (
                                            <InputAdornment position="end">
                                                <QuickFilterClear
                                                    edge="end"
                                                    size="small"
                                                    aria-label="Clear search"
                                                    material={{
                                                        sx: {
                                                            marginRight: -0.75,
                                                        },
                                                    }}
                                                >
                                                    <CancelIcon fontSize="small" />
                                                </QuickFilterClear>
                                            </InputAdornment>
                                        ) : null,
                                        ...controlProps.slotProps?.input,
                                    },
                                    ...controlProps.slotProps,
                                }}
                            />
                        )
                    }}
                />
            </StyledQuickFilter>
        </StyledWrapper>
    )
}
